import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Grid, Box, Typography, alpha, IconButton
} from '@mui/material';
import { IonIcon } from '@ionic/react';
import { close, save, pricetag } from 'ionicons/icons';
import * as TaxService from '../../../api/tax.service';
import { Tax } from '../../../models/tax';

interface TaxModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    tax?: Tax | null; // Pass the tax data directly
}

const TaxModal: React.FC<TaxModalProps> = ({ open, onClose, onSuccess, tax }) => {
    const isEdit = Boolean(tax);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<any>({
        tax_code: '',
        name: '',
        rate: '0.00',
        tax_type: ''
    });

    useEffect(() => {
        if (open && tax) {
            // Use the passed tax data for editing
            setFormData({
                name: tax.name,
                rate: tax.rate,
                tax_type: tax.tax_type,
                geacloud_taxes_id: tax.geacloud_taxes_id
            });
        } else if (open && !tax) {
            // Reset form for new tax
            setFormData({
                tax_code: '',
                name: '',
                rate: '0.00',
                tax_type: ''
            });
        }
    }, [open, tax]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEdit && tax) {
                // For update, include geacloud_taxes_id in the payload
                await TaxService.updateTax({
                    ...formData,
                    geacloud_taxes_id: tax.geacloud_taxes_id
                });
            } else {
                await TaxService.createTax(formData);
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Error saving tax", error);
            alert('Failed to save tax. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(139, 92, 246, 0.2)'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                p: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IonIcon icon={pricetag} style={{ fontSize: '2rem' }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {isEdit ? 'Edit Tax' : 'New Tax'}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                {isEdit ? 'Update tax information' : 'Create a new tax'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <IonIcon icon={close} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Form Content */}
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                    <Grid container spacing={3}>
                        {/* Tax Name */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                                Tax Name *
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="e.g., Value Added Tax"
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Tax Rate */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                                Tax Rate (%) *
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                name="rate"
                                type="number"
                                value={formData.rate}
                                onChange={handleChange}
                                placeholder="5.00"
                                disabled={loading}
                                inputProps={{ min: 0, step: 0.01, max: 100 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: alpha('#8b5cf6', 0.05),
                                        '&:hover': {
                                            bgcolor: alpha('#8b5cf6', 0.08)
                                        }
                                    },
                                    '& input': {
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        color: '#8b5cf6'
                                    }
                                }}
                            />
                        </Grid>

                        {/* Tax Type */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                                Tax Type *
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                name="tax_type"
                                value={formData.tax_type}
                                onChange={handleChange}
                                placeholder="e.g., VAT, Sales Tax"
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* Actions */}
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            color: '#64748b',
                            '&:hover': {
                                bgcolor: '#f1f5f9'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={<IonIcon icon={save} />}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                            fontWeight: 600,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                boxShadow: '0 6px 16px rgba(139, 92, 246, 0.4)'
                            }
                        }}
                    >
                        {loading ? 'Saving...' : isEdit ? 'Update Tax' : 'Create Tax'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default TaxModal;
